import SEO from "@/components/SEO";
import {
  CTAButton,
  H2,
  HeroH1,
  P,
  SeoPageShell,
} from "../seo/SeoPageLayout";
import AnalysisExampleBlock from "@/components/seo/AnalysisExampleBlock";
import CrossLinkFooter from "@/components/seo/AnalysisCrossLinkFooter";

const Bullet = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <li className="mt-3 text-[17px] leading-[1.7] text-white/75">
    <span className="text-white font-semibold">{title}</span> {children}
  </li>
);

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How can I tell if my feelings are being invalidated over text?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Your reaction gets corrected before it gets heard. Phrases like 'you're overreacting' or 'that's not a big deal' arrive before any actual engagement with what you said.",
      },
    },
    {
      "@type": "Question",
      name: "What does it mean when a feeling gets 'relabeled as a flaw'?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Being upset becomes evidence you're 'too sensitive,' 'dramatic,' or 'difficult' — the emotion itself becomes the accusation, instead of a valid response to what happened.",
      },
    },
    {
      "@type": "Question",
      name: "Is comparing me to other people a form of invalidation?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Yes. 'Other people would be fine with this' swaps out your actual experience for a hypothetical one that's more convenient, and skips acknowledging what you feel.",
      },
    },
    {
      "@type": "Question",
      name: "Why does the conversation keep shifting to my reaction instead of the event?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A conversation about what happened quietly becomes a conversation about how you responded to what happened. Once you're defending your right to feel something, the original event stops being discussed.",
      },
    },
  ],
};

const InvalidationPatternsInTexts = () => {
  return (
    <SeoPageShell>
      <SEO
        title="Invalidation Patterns in Text Messages — See It Named, Free"
        description="Paste the message. See exactly which invalidation pattern it is — dismissal, minimizing, denial — named in seconds. Free, private, instant."
        path="/analysis/invalidation-patterns-in-texts"
        jsonLd={FAQ_SCHEMA}
      />

      <HeroH1>Analyzing Invalidation Patterns in Text Messages</HeroH1>

      <H2>The Quick Diagnostic</H2>
      <P>Invalidation rarely announces itself. Look for these shapes instead:</P>
      <ul className="list-none pl-0 mt-2">
        <Bullet title="Your reaction gets corrected before it gets heard.">
          "You're overreacting" or "that's not a big deal" arrives before any actual engagement
          with what you said.
        </Bullet>
        <Bullet title="The feeling gets relabeled as a flaw.">
          Being upset becomes evidence you're "too sensitive," "dramatic," or "difficult" — the
          emotion itself becomes the accusation.
        </Bullet>
        <Bullet title="Comparison replaces acknowledgment.">
          "Other people would be fine with this" swaps out your actual experience for a
          hypothetical one that's more convenient.
        </Bullet>
        <Bullet title="The topic changes to your reaction, not the event.">
          A conversation about what happened quietly becomes a conversation about how you responded
          to what happened.
        </Bullet>
      </ul>

      <H2>What it looks like in a real exchange</H2>
      <AnalysisExampleBlock
        turns={[
          { speaker: "you", text: "It really hurt when you said that in front of everyone." },
          {
            speaker: "them",
            tone: "toxic",
            text: "I was just joking, you're being way too sensitive about this.",
          },
        ]}
        mechanismLabel="Pattern: Feeling → Flaw Redirection"
        mechanismDescription="The actual content of what was said never gets addressed. Instead, the fact that it hurt becomes the new subject — framed as your problem to fix, not something that happened to you."
      />

      <H2>The breakdown</H2>
      <P>
        Invalidation works by relocating the conversation. Instead of "was that hurtful," the
        question quietly becomes "is your hurt reasonable" — and that second question puts you on
        trial instead of the original moment. Once you're defending your right to feel something,
        the actual event stops being discussed at all. This is what makes invalidation so
        disorienting in the moment: you came in with one grievance and somehow end up litigating
        your own emotional credibility instead.
      </P>

      <H2>See it in your own conversation</H2>
      <P>
        You just read a fictional example. If a real exchange has been sitting with you, paste it
        into the analyzer — no signup required for your first look.
      </P>
      <CTAButton />

      <CrossLinkFooter current="invalidation" />
    </SeoPageShell>
  );
};

export default InvalidationPatternsInTexts;
